import { DASHBOARD_PROJECTS } from "../../fixtures";
import {
  createAdminApiFailure,
  createAdminApiSuccess,
  normalizeAdminApiError,
} from "../../../shared/api/adminApiErrors";
import type { AdminApiResult } from "../../../shared/api/types/adminApi";
import { buildApiUrl } from "../../../../services/apiUrl";
import { authenticatedFetch } from "../../../../lib/apiClient";
import type {
  DashboardProjectArchiveDownloadResultDto,
  DashboardProjectCreateRequestDto,
  DashboardProjectCreateMutationResultDto,
  DashboardProjectDetailResultDto,
  DashboardProjectsListDto,
  DashboardProjectRecordDto,
  DashboardProjectStatusUpdateMutationResultDto,
  DashboardWorkspaceSnapshotDto,
} from "../types/projects.api";
export { validateDashboardProjectsListDto } from "../types/projects.api";
import {
  fetchViewerAuthoringDraft,
  fetchViewerAuthoringStatus,
} from "../../../../Viewer/authoring/api/viewerAuthoringApi";

export const PROJECTS_WORKSPACE_TARGET = {
  companyId: "weijia",
  productId: "pergola-pro",
  companyName: "Weijia Construction",
  projectName: "Sunshade Louver Pergola",
} as const;

function sanitizeProjectIdPart(value: string): string {
  const sanitized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return sanitized.length > 0 ? sanitized : "new-project";
}

function toProjectRecord(
  project: (typeof DASHBOARD_PROJECTS)[number],
): DashboardProjectRecordDto {
  return {
    id: project.id,
    title: project.name,
    companyName: project.companyName,
    updatedAtLabel: project.updatedAtLabel,
    status: project.status,
    collaborators: [...project.collaborators],
    createdAtIso: null,
    createdAtLabel: null,
    animatorAssignedLabel: null,
    companyId: null,
    productId: null,
  };
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  return "Failed to load workspace snapshot.";
}

export function getSeededProjectRecords(): DashboardProjectRecordDto[] {
  return DASHBOARD_PROJECTS.map(toProjectRecord);
}

export function getDashboardProjectsListData(): DashboardProjectsListDto {
  return {
    items: getSeededProjectRecords(),
  };
}

export async function fetchDashboardProjectsList(): Promise<
  AdminApiResult<DashboardProjectsListDto>
> {
  try {
    const response = await authenticatedFetch(buildApiUrl("/api/admin/projects"), {
      method: "GET",
    });
    const payload = (await response.json()) as
      | { success: true; data: DashboardProjectsListDto }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "PROJECTS_LIST_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to load projects right now."
            : payload.error?.message ?? "Unable to load projects right now.",
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    console.error("[dashboard.projects] PROJECTS_LIST_ERROR", error);
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECTS_LIST_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to load projects right now.",
      }),
    );
  }
}

export function createProjectRecord(
  input: DashboardProjectCreateRequestDto,
): DashboardProjectRecordDto {
  return {
    id: `${sanitizeProjectIdPart(input.title)}-${Date.now().toString(36)}`,
    title: input.title.trim(),
    companyName: input.companyName.trim(),
    updatedAtLabel: "Updated just now",
    status: "in_production",
    collaborators: input.collaborators?.length ? input.collaborators : ["ME"],
    createdAtIso: null,
    createdAtLabel: null,
    animatorAssignedLabel: null,
    companyId: null,
    productId: null,
  };
}

function hasValidProjectTitle(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

export async function submitDashboardProjectCreate(
  request: DashboardProjectCreateRequestDto,
): Promise<AdminApiResult<DashboardProjectCreateMutationResultDto>> {
  if (!hasValidProjectTitle(request.title)) {
    console.error("[dashboard.projects] PROJECT_CREATE_VALIDATION_ERROR", {
      title: request.title,
      companyName: request.companyName,
    });
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECT_TITLE_REQUIRED",
        message: "Project title is required before creating a project.",
      }),
    );
  }

  try {
    const response = await authenticatedFetch(buildApiUrl("/api/admin/projects"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        title: request.title.trim(),
        companyName: request.companyName.trim(),
        collaborators: request.collaborators ?? [],
        entitlementId: request.entitlementId ?? null,
      }),
    });
    const payload = (await response.json()) as
      | { success: true; data: DashboardProjectCreateMutationResultDto }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "PROJECT_CREATE_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to create this project right now."
            : payload.error?.message ?? "Unable to create this project right now.",
        }),
      );
    }

    console.info("[dashboard.projects] PROJECT_INSERT_RESULT", {
      title: request.title.trim(),
      projectId: payload.data.item.id,
      workspaceCompanyName: payload.data.item.companyName,
    });
    return createAdminApiSuccess(payload.data);
  } catch (error) {
    console.error("[dashboard.projects] PROJECT_CREATE_ERROR", error);
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECT_CREATE_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to create this project right now.",
      }),
    );
  }
}

export async function getProjectsWorkspaceSnapshot(): Promise<DashboardWorkspaceSnapshotDto> {
  try {
    const [status, draft] = await Promise.all([
      fetchViewerAuthoringStatus(
        PROJECTS_WORKSPACE_TARGET.companyId,
        PROJECTS_WORKSPACE_TARGET.productId,
      ),
      fetchViewerAuthoringDraft(
        PROJECTS_WORKSPACE_TARGET.companyId,
        PROJECTS_WORKSPACE_TARGET.productId,
      ),
    ]);

    return {
      status: "ready",
      error: null,
      companyId: PROJECTS_WORKSPACE_TARGET.companyId,
      productId: PROJECTS_WORKSPACE_TARGET.productId,
      companyName: PROJECTS_WORKSPACE_TARGET.companyName,
      projectName: PROJECTS_WORKSPACE_TARGET.projectName,
      backendState: status.state,
      previewUrl: status.previewUrl,
      liveUrl: status.liveUrl,
      draftRevision: draft?.revision ?? null,
      draftStepCount: Array.isArray(draft?.timelineSteps)
        ? draft.timelineSteps.length
        : null,
      hasLiveDeployment: status.hasLiveDeployment,
      updatedAtLabel: status.updatedAt
        ? new Date(status.updatedAt).toLocaleString()
        : null,
    };
  } catch (error) {
    return {
      status: "error",
      error: getErrorMessage(error),
      companyId: PROJECTS_WORKSPACE_TARGET.companyId,
      productId: PROJECTS_WORKSPACE_TARGET.productId,
      companyName: PROJECTS_WORKSPACE_TARGET.companyName,
      projectName: PROJECTS_WORKSPACE_TARGET.projectName,
      backendState: null,
      previewUrl: null,
      liveUrl: null,
      draftRevision: null,
      draftStepCount: null,
      hasLiveDeployment: false,
      updatedAtLabel: null,
    };
  }
}

export async function fetchDashboardProjectDetail(
  projectId: string,
): Promise<AdminApiResult<DashboardProjectDetailResultDto>> {
  try {
    const response = await authenticatedFetch(
      buildApiUrl(`/api/admin/projects/${encodeURIComponent(projectId)}`),
      {
        method: "GET",
      },
    );
    const payload = (await response.json()) as
      | { success: true; data: DashboardProjectDetailResultDto }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "PROJECT_DETAIL_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to load project details right now."
            : payload.error?.message ?? "Unable to load project details right now.",
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    console.error("[dashboard.projects] PROJECT_DETAIL_ERROR", error);
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECT_DETAIL_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to load project details right now.",
      }),
    );
  }
}

export async function updateDashboardProjectStatus(
  projectId: string,
  status: DashboardProjectRecordDto["status"],
): Promise<AdminApiResult<DashboardProjectStatusUpdateMutationResultDto>> {
  try {
    const response = await authenticatedFetch(
      buildApiUrl(`/api/admin/projects/${encodeURIComponent(projectId)}`),
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      },
    );
    const payload = (await response.json()) as
      | { success: true; data: DashboardProjectStatusUpdateMutationResultDto }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "PROJECT_STATUS_UPDATE_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to update project status right now."
            : payload.error?.message ?? "Unable to update project status right now.",
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    console.error("[dashboard.projects] PROJECT_STATUS_UPDATE_ERROR", error);
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECT_STATUS_UPDATE_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to update project status right now.",
      }),
    );
  }
}

function getArchiveFileName(
  contentDisposition: string | null,
  projectName: string,
): string {
  const match = contentDisposition?.match(/filename="?([^"]+)"?/i);
  if (match?.[1]) {
    return match[1];
  }

  return `${sanitizeProjectIdPart(projectName)}-assets.zip`;
}

export async function downloadDashboardProjectArchive(
  projectId: string,
  projectName: string,
): Promise<AdminApiResult<DashboardProjectArchiveDownloadResultDto>> {
  try {
    const response = await authenticatedFetch(
      buildApiUrl(`/api/admin/projects/${encodeURIComponent(projectId)}/archive`),
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      let errorMessage = "Unable to download the project archive right now.";

      try {
        const payload = (await response.json()) as {
          success?: boolean;
          error?: { code?: string | null; message?: string | null };
        };
        errorMessage = payload.error?.message ?? errorMessage;
      } catch {
        // Ignore parse failures for binary responses.
      }

      return createAdminApiFailure(
        normalizeAdminApiError({
          code: "PROJECT_ARCHIVE_DOWNLOAD_FAILED",
          message: errorMessage,
        }),
      );
    }

    const fileName = getArchiveFileName(
      response.headers.get("content-disposition"),
      projectName,
    );
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);

    return createAdminApiSuccess({ fileName });
  } catch (error) {
    console.error("[dashboard.projects] PROJECT_ARCHIVE_DOWNLOAD_ERROR", error);
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECT_ARCHIVE_DOWNLOAD_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to download the project archive right now.",
      }),
    );
  }
}

export async function submitDashboardProjectDelete(
  projectId: string,
): Promise<AdminApiResult<{ deleted: boolean }>> {
  const trimmedId = projectId.trim();
  if (!trimmedId) {
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECT_DELETE_VALIDATION",
        message: "Project id is required to delete a project.",
      }),
    );
  }

  try {
    const response = await authenticatedFetch(
      buildApiUrl(`/api/admin/projects/${encodeURIComponent(trimmedId)}`),
      {
        method: "DELETE",
      },
    );
    const payload = (await response.json()) as
      | { success: true; data: { deleted: boolean } }
      | { success: false; error?: { code?: string | null; message?: string | null } };

    if (!response.ok || !payload.success) {
      return createAdminApiFailure(
        normalizeAdminApiError({
          code: payload.success ? "PROJECT_DELETE_FAILED" : payload.error?.code,
          message: payload.success
            ? "Unable to delete this project right now."
            : payload.error?.message ?? "Unable to delete this project right now.",
        }),
      );
    }

    return createAdminApiSuccess(payload.data);
  } catch (error) {
    console.error("[dashboard.projects] PROJECT_DELETE_ERROR", error);
    return createAdminApiFailure(
      normalizeAdminApiError({
        code: "PROJECT_DELETE_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Unable to delete this project right now.",
      }),
    );
  }
}
